import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { first, map, tap } from 'rxjs/operators';

import { KeyGeneratorService } from '@atmi/core';
import { ResponsePaginateSuccess, ResponseSuccess } from '@atmi/core/models';
import { generateIndexQueryString, generatePlainQueryParamsFromObject, IndexQueryStringType } from '@atmi/core/utils';

import { Collection } from '../models/collection';
import { CollectionModuleConfiguration } from '../models/configuration';
import { CollectionConfigurationService } from '../token';
import { addCollection, deleteCollection, setCollection, updateCollection } from '@src/app/store/collection.repository';

@Injectable({
  providedIn: 'root',
})
export class CollectionApiService {

  private readonly path = `${this.config.basePath}/collezioni`;

  constructor(
    @Inject(CollectionConfigurationService) private readonly config: CollectionModuleConfiguration,
    private readonly http: HttpClient,
    private readonly keyGeneratorService: KeyGeneratorService,
  ) {
  }


  getAll() {
    return this.http.get<ResponsePaginateSuccess<Collection[]>>(this.path, {})
      .pipe(
        map((res) => ({ data: res.data, count: res.count })),
        tap((res) => {
          setCollection(res.data);
          return res;
        }),
        first(),
      );
  }

  index(queryString?: IndexQueryStringType) {
    const params = generateIndexQueryString(queryString);

    return this.http.get<ResponsePaginateSuccess<Collection[]>>(this.path, { params });
  }

  get(id: string) {
    return this.http.get<ResponseSuccess<Collection>>(`${this.path}/${id}`).pipe(
      map((res) => res.data),
    );
  }

  post(collection: Partial<Collection>) {
    return this.http.post<ResponseSuccess<Collection>>(this.path, collection).pipe(
      map((res) => res.data),
      tap((collection) => {
        addCollection(collection);
        return collection;
      }),
      first(),
    );
  }

  put(collection: Collection) {
    return this.http.put<ResponseSuccess<Collection>>(`${this.path}/${collection.id}`, collection).pipe(
      map((res) => res.data),
      tap((collection) => {
        updateCollection(collection.id, collection);
        return collection;
      }),
      first(),
    );
  }

  remove(id: string) {
    return this.http.delete(`${this.path}/${id}`)
      .pipe(tap((collection) => {
        deleteCollection(id);
        return collection;
      }), first());
  }

  exportCollection(id: string, template: string = 'default', format: string = 'pdf') {
    const key = this.keyGeneratorService.getBinaryKeyGenerator();

    let url = `${this.path}/${id}/export?template=${template}&format=${format}`;
    if (key) {
      url += `&key=${key}`;
    }

    return this.http.get(url, { responseType: 'blob' });
  }

  exportList(
    template: string = 'default',
    format: string = 'pdf',
    queryString: IndexQueryStringType,
    selection?: string[],
  ) {
    const key = this.keyGeneratorService.getBinaryKeyGenerator();
    const params = generatePlainQueryParamsFromObject({ ...queryString, selection });

    let url = `${this.path}/export?template=${template}&format=${format}&${params}`;
    if (key) {
      url += `&key=${key}`;
    }

    return this.http.get(url, { responseType: 'blob' });
  }
}
